// src/components/sensors/useLiveSensorRegistry.js
// FlowSense sensor tables — sticky sensor registry + live clock — NEW FILE
// ═══════════════════════════════════════════════════════════════════════════
// useNow(intervalMs)
//   One shared clock per interval for every table on the page. Pauses while the
//   tab is hidden and fires immediately when it becomes visible again.
//
// useLiveSensorRegistry({ sensors, readings, thresholds, includeUnregistered })
//   Returns one row per device, built from the UNION of the `sensors` prop and
//   the keys of `readings`, matched case-insensitively. Fixes:
//     • rows that appeared from live readings and then vanished when the sensor
//       list loaded (ReadingTable only used readings while `sensors` was empty);
//     • rows that vanished while the page re-fetched sensors (kept for retainMs);
//     • a fresher live reading being replaced by an older REST reading
//       (a reading older than the one already shown is ignored);
//     • a recent reading (measured ≤ onlineMs ago) counts as heard when it ARRIVES, so a
//       slow / buffered post doesn't make a transmitting node look late.
//   Row objects are reused while nothing about them changed, so memoised rows
//   don't re-render on every clock tick.
// ═══════════════════════════════════════════════════════════════════════════
import { useMemo, useRef, useSyncExternalStore } from 'react';
import {
  LIVE_STATE_META,
  mergeThresholds,
  normalizeId,
  readingSignature,
  readingTimeMs,
  resolveLiveState,
  sensorActivityMs,
  sensorIdOf,
} from './sensorLiveStatus';

// ── shared clock ────────────────────────────────────────────────────────────
const clocks = new Map(); // intervalMs -> { now, listeners:Set, timer }

const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

const getClock = (intervalMs) => {
  let c = clocks.get(intervalMs);
  if (!c) {
    c = { now: Date.now(), listeners: new Set(), timer: null, onVis: null };
    clocks.set(intervalMs, c);
  }
  return c;
};

const emit = (c) => {
  c.now = Date.now();
  c.listeners.forEach((l) => l());
};

const subscribeClock = (intervalMs) => (listener) => {
  const c = getClock(intervalMs);
  c.listeners.add(listener);
  if (!c.timer) {
    c.timer = setInterval(() => { if (visible()) emit(c); }, intervalMs);
    c.onVis = () => { if (visible()) emit(c); };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', c.onVis);
  }
  return () => {
    c.listeners.delete(listener);
    if (!c.listeners.size) {
      clearInterval(c.timer);
      c.timer = null;
      if (typeof document !== 'undefined' && c.onVis) document.removeEventListener('visibilitychange', c.onVis);
      clocks.delete(intervalMs);
    }
  };
};

/** Epoch ms that updates every `intervalMs` (default 5 s) while the tab is visible. */
export function useNow(intervalMs = 5000) {
  const subscribe = useMemo(() => subscribeClock(intervalMs), [intervalMs]);
  return useSyncExternalStore(subscribe, () => getClock(intervalMs).now, () => Date.now());
}

// ── registry ────────────────────────────────────────────────────────────────
const sameRow = (a, b) =>
  a && b &&
  a.id === b.id && a.sensor === b.sensor && a.reading === b.reading &&
  a.liveState === b.liveState && a.lastHeardMs === b.lastHeardMs && a.measuredMs === b.measuredMs &&
  a.arrivals === b.arrivals && a.registered === b.registered && a.retained === b.retained &&
  a.clockSkew === b.clockSkew && a.lastArrivalMs === b.lastArrivalMs;

/**
 * @param {object}   p
 * @param {object[]} p.sensors              registered sensors for this table
 * @param {object}   p.readings             { [deviceId]: latest reading }
 * @param {number}   p.now                  from useNow()
 * @param {object}  [p.thresholds]          overrides for DEFAULT_LIVE_THRESHOLDS
 * @param {boolean} [p.includeUnregistered] show devices that send readings but are not in `sensors` (default true)
 * @returns {{ rows: object[], byKey: Map<string, object>, counts: object }}
 */
export function useLiveSensorRegistry({ sensors, readings, now, thresholds, includeUnregistered = true }) {
  const t = useMemo(() => mergeThresholds(thresholds), [thresholds]);
  const trackRef = useRef(new Map());   // key -> { sig, reading, readingMs, heardMs, arrivals, sourceId }
  const retainRef = useRef(new Map());  // key -> { sensor, listedAt }
  const rowCacheRef = useRef(new Map()); // key -> last row object

  // 1) Track readings. Idempotent for the same `readings` object (StrictMode safe):
  //    only a different, not-older reading counts as an arrival.
  const tracked = useMemo(() => {
    const track = trackRef.current;
    const at = Date.now();
    const list = readings && typeof readings === 'object' ? Object.keys(readings) : [];
    for (const rawKey of list) {
      const r = readings[rawKey];
      if (!r || typeof r !== 'object') continue;
      const key = normalizeId(rawKey);
      if (!key) continue;
      const sig = readingSignature(r);
      const prev = track.get(key);
      if (!prev) {
        // First sight (initial REST load or first live message): use the reading's own time only.
        track.set(key, { sig, reading: r, readingMs: readingTimeMs(r), heardMs: null, arrivals: 0, lastArrivalMs: null, sourceId: rawKey });
        continue;
      }
      if (prev.sig === sig) {
        if (prev.reading !== r) prev.reading = r; // same reading, new object (e.g. status field added)
        continue;
      }
      const rMs = readingTimeMs(r);
      if (rMs !== null && prev.readingMs !== null && rMs < prev.readingMs) continue; // older → keep the newer one
      // A newer reading counts as "heard now" only when it was measured recently (≤ onlineMs).
      // An old one (REST re-fetch, or the start of a node's buffer replay) is shown, but the
      // status follows its measured time — the node's next fresh post brings it back online.
      const credited = rMs === null || at - rMs <= t.onlineMs;
      track.set(key, {
        sig, reading: r, readingMs: rMs,
        heardMs: credited ? at : prev.heardMs,
        arrivals: credited ? prev.arrivals + 1 : prev.arrivals,
        lastArrivalMs: credited ? at : prev.lastArrivalMs,
        sourceId: rawKey,
      });
    }
    return { track }; // new wrapper per readings change → drives the rows memo below
  }, [readings, t]);

  // 2) Build rows: registered sensors ∪ retained sensors ∪ reading-only devices.
  return useMemo(() => {
    const { track } = tracked;
    const retain = retainRef.current;
    const cache = rowCacheRef.current;
    const seeds = new Map(); // key -> { id, sensor, registered, retained }

    (Array.isArray(sensors) ? sensors : []).forEach((s) => {
      const id = sensorIdOf(s);
      const key = normalizeId(id);
      if (!key || seeds.has(key)) return;
      seeds.set(key, { id: String(id), sensor: s, registered: true, retained: false });
      retain.set(key, { sensor: s, listedAt: now });
    });

    retain.forEach((v, key) => {
      if (seeds.has(key)) return;
      if (now - v.listedAt <= t.retainMs) {
        seeds.set(key, { id: String(sensorIdOf(v.sensor)), sensor: v.sensor, registered: true, retained: true });
      } else {
        retain.delete(key);
      }
    });

    if (includeUnregistered) {
      track.forEach((entry, key) => {
        if (!seeds.has(key)) {
          seeds.set(key, { id: String(entry.sourceId), sensor: { device_id: String(entry.sourceId) }, registered: false, retained: false });
        }
      });
    }

    const rows = [];
    const byKey = new Map();
    const counts = { total: 0, online: 0, offline: 0, transmitting: 0, delayed: 0, no_data: 0, unregistered: 0 };

    seeds.forEach((seed, key) => {
      const entry = track.get(key);
      const reading = entry ? entry.reading : seed.sensor?.latest_reading || null;
      const measuredMs = entry ? entry.readingMs : readingTimeMs(reading);
      let clockSkew = false;
      if (measuredMs !== null && measuredMs > now + t.futureSkewMs) clockSkew = true;
      const measuredForStatus = measuredMs === null ? null : Math.min(measuredMs, now);
      const heard = [entry?.heardMs ?? null, measuredForStatus, sensorActivityMs(seed.sensor)]
        .filter((v) => v !== null && Number.isFinite(v));
      const lastHeardMs = heard.length ? Math.min(Math.max(...heard), now) : null;
      const liveState = resolveLiveState(lastHeardMs, now, t);

      const next = {
        key,
        id: seed.id,
        sensor: seed.sensor,
        reading,
        registered: seed.registered,
        retained: seed.retained,
        measuredMs,
        lastHeardMs,
        liveState,
        isOnline: LIVE_STATE_META[liveState].isOnline,
        arrivals: entry ? entry.arrivals : 0,
        lastArrivalMs: entry ? entry.lastArrivalMs : null,
        clockSkew,
      };
      const prev = cache.get(key);
      const row = sameRow(prev, next) ? prev : next;
      cache.set(key, row);
      rows.push(row);
      byKey.set(key, row);

      counts.total += 1;
      if (row.isOnline) counts.online += 1; else counts.offline += 1;
      if (liveState === 'transmitting') counts.transmitting += 1;
      if (liveState === 'delayed') counts.delayed += 1;
      if (liveState === 'no_data') counts.no_data += 1;
      if (!row.registered) counts.unregistered += 1;
    });

    cache.forEach((_, key) => { if (!byKey.has(key)) cache.delete(key); });
    return { rows, byKey, counts, thresholds: t };
  }, [sensors, tracked, now, t, includeUnregistered]);
}

/**
 * Rolling numeric history per device, fed from the registry rows (oldest → newest).
 * A value is appended only when the row's reading changes (value or measured time).
 * Each device's array is replaced on append, so memoised rows see a new prop only then.
 */
export function useRollingValues(rows, valueOf, max = 48) {
  const storeRef = useRef(new Map()); // key -> { sig, values }
  const versionRef = useRef(0);
  const out = useMemo(() => {
    const store = storeRef.current;
    let changed = false;
    rows.forEach((row) => {
      const v = valueOf(row);
      if (v === null || v === undefined || !Number.isFinite(v)) return;
      const sig = `${row.measuredMs ?? ''}|${v}`;
      const prev = store.get(row.key);
      if (prev && prev.sig === sig) return;
      const values = prev ? [...prev.values, v].slice(-max) : [v];
      store.set(row.key, { sig, values });
      changed = true;
    });
    if (changed) versionRef.current += 1;
    return store;
    // valueOf is expected to be a module-level function
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, max]);
  return { store: out, version: versionRef.current };
}
